import api from './api';

// TypeScript Interfaces
export interface ODOSModule {
  _id: string;
  moduleId: string;
  name: string;
  description: string;
  category: string;
  pwsReference?: {
    section: string;
    page: number;
    requirement: string;
  };
  status: 'planning' | 'in-development' | 'testing' | 'deployed' | 'active' | 'maintenance' | 'deprecated';
  features: ODOSFeature[];
  technicalStack?: {
    frontend: string[];
    backend: string[];
    database: string[];
    infrastructure: string[];
    integrations: string[];
  };
  compliance?: {
    fisma: boolean;
    fedramp: boolean;
    section508: boolean;
    soc2: boolean;
    nist?: {
      framework: string;
      controls: string[];
    };
  };
  accessControl?: {
    requiredRoles: string[];
    permissions: ODOSPermission[];
  };
  dependencies?: ODOSDependency[];
  deliverables?: ODOSDeliverable[];
  metrics?: ODOSMetric[];
  team?: {
    lead?: string;
    members?: ODOSTeamMember[];
    stakeholders?: ODOSStakeholder[];
  };
  documentation?: {
    readme?: string;
    architecture?: string;
    userGuide?: string;
    apiDocs?: string;
    deploymentGuide?: string;
    links?: ODOSLink[];
  };
  version?: {
    major: number;
    minor: number;
    patch: number;
  };
  plannedStartDate?: Date;
  actualStartDate?: Date;
  plannedEndDate?: Date;
  actualEndDate?: Date;
  lastDeploymentDate?: Date;
  budget?: {
    allocated: number;
    spent: number;
    currency: string;
  };
  risks?: ODOSRisk[];
  issues?: ODOSIssue[];
  tags?: string[];
  isActive: boolean;
  isPublic: boolean;
  createdAt: Date;
  updatedAt: Date;
  versionString?: string;
  completionPercentage?: number;
  sortOrder?: number;
  priority?: 'critical' | 'high' | 'medium' | 'low';
}

export interface ODOSFeature {
  _id?: string;
  name: string;
  title?: string;
  description: string;
  userStory?: string;
  userStoryDetails?: {
    role?: string;
    need?: string;
    benefit?: string;
  };
  originalDescription?: string;
  status: 'planned' | 'in-progress' | 'completed' | 'deferred';
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface ODOSPermission {
  action: string;
  resource: string;
  roles: string[];
}

export interface ODOSDependency {
  moduleId: string;
  moduleName: string;
  type: 'required' | 'optional' | 'recommended';
  version?: string;
}

export interface ODOSDeliverable {
  _id?: string;
  name: string;
  description: string;
  type: 'document' | 'code' | 'report' | 'dashboard' | 'api' | 'service';
  dueDate?: Date;
  status: 'not-started' | 'in-progress' | 'completed' | 'delivered';
  clin?: string;
}

export interface ODOSMetric {
  _id?: string;
  name: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
}

export interface ODOSTeamMember {
  user: string;
  role: string;
  responsibilities: string[];
}

export interface ODOSStakeholder {
  name: string;
  organization: string;
  role: string;
  email: string;
}

export interface ODOSLink {
  title: string;
  url: string;
  type: string;
}

export interface ODOSRisk {
  _id?: string;
  description: string;
  impact: 'low' | 'medium' | 'high' | 'critical';
  probability: 'low' | 'medium' | 'high';
  mitigation: string;
  status: 'identified' | 'mitigated' | 'accepted' | 'resolved';
}

export interface ODOSIssue {
  _id?: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  assignedTo?: string;
  createdAt: Date;
  resolvedAt?: Date;
}

export interface ODOSActivity {
  _id: string;
  activityId: string;
  title: string;
  description: string;
  module: string | ODOSModule;
  type: string;
  status: 'backlog' | 'planned' | 'in-progress' | 'blocked' | 'review' | 'completed' | 'cancelled';
  priority: 'critical' | 'high' | 'medium' | 'low';
  assignedTo: string;
  assignedBy?: string;
  estimatedHours?: number;
  actualHours?: number;
  startDate?: Date;
  dueDate?: Date;
  completedDate?: Date;
  sprint?: {
    number: number;
    name: string;
    startDate: Date;
    endDate: Date;
  };
  dependencies?: ODOSActivityDependency[];
  acceptanceCriteria?: ODOSAcceptanceCriteria[];
  testing?: ODOSTesting;
  security?: ODOSSecurity;
  comments?: ODOSComment[];
  attachments?: ODOSAttachment[];
  blockers?: ODOSBlocker[];
  tags?: string[];
  labels?: string[];
  createdAt: Date;
  updatedAt: Date;
  completionPercentage?: number;
  isOverdue?: boolean;
}

export interface ODOSActivityDependency {
  activity: string;
  type: 'blocks' | 'blocked-by' | 'relates-to';
}

export interface ODOSAcceptanceCriteria {
  _id?: string;
  description: string;
  completed: boolean;
  completedBy?: string;
  completedAt?: Date;
}

export interface ODOSTesting {
  required: boolean;
  testCases?: ODOSTestCase[];
  testResults?: string;
}

export interface ODOSTestCase {
  name: string;
  status: 'pending' | 'passed' | 'failed' | 'blocked';
  testedBy?: string;
  testedAt?: Date;
  notes?: string;
}

export interface ODOSSecurity {
  scanRequired: boolean;
  scanCompleted: boolean;
  vulnerabilities?: ODOSVulnerability[];
  complianceChecks?: ODOSComplianceCheck[];
}

export interface ODOSVulnerability {
  severity: string;
  description: string;
  status: string;
}

export interface ODOSComplianceCheck {
  standard: string;
  passed: boolean;
  notes: string;
}

export interface ODOSComment {
  _id?: string;
  user: string;
  text: string;
  createdAt: Date;
}

export interface ODOSAttachment {
  _id?: string;
  name: string;
  url: string;
  type: string;
  uploadedBy: string;
  uploadedAt: Date;
}

export interface ODOSBlocker {
  _id?: string;
  description: string;
  status: 'active' | 'resolved';
  reportedBy: string;
  reportedAt: Date;
  resolvedAt?: Date;
  resolution?: string;
}

export interface ODOSDashboardStats {
  totalModules: number;
  activeModules: number;
  inDevelopment: number;
  planning: number;
  categories: { _id: string; count: number }[];
  recentActivities: ODOSActivity[];
}

export interface ODOSActivityStats {
  totalActivities: number;
  inProgress: number;
  completed: number;
  blocked: number;
  overdue: number;
  byType: { _id: string; count: number }[];
  byPriority: { _id: string; count: number }[];
}

// Module API Functions
export const odosModuleService = {
  // Get all modules
  getModules: async (params?: { category?: string; status?: string; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    
    const response = await api.get(`/odos/modules?${queryParams.toString()}`);
    return response.data;
  },

  // Get single module
  getModule: async (id: string) => {
    const response = await api.get(`/odos/modules/${id}`);
    return response.data;
  },

  // Create module
  createModule: async (moduleData: Partial<ODOSModule>) => {
    const response = await api.post('/odos/modules', moduleData);
    return response.data;
  },

  // Update module
  updateModule: async (id: string, moduleData: Partial<ODOSModule>) => {
    const response = await api.put(`/odos/modules/${id}`, moduleData);
    return response.data;
  },

  // Delete module
  deleteModule: async (id: string) => {
    const response = await api.delete(`/odos/modules/${id}`);
    return response.data;
  },

  // Get modules by category
  getModulesByCategory: async (category: string) => {
    const response = await api.get(`/odos/modules/category/${category}`);
    return response.data;
  },

  // Get modules by status
  getModulesByStatus: async (status: string) => {
    const response = await api.get(`/odos/modules/status/${status}`);
    return response.data;
  },

  // Get user's modules
  getUserModules: async () => {
    const response = await api.get('/odos/modules/user/my-modules');
    return response.data;
  },

  // Add feature
  addFeature: async (moduleId: string, feature: ODOSFeature) => {
    const response = await api.post(`/odos/modules/${moduleId}/features`, feature);
    return response.data;
  },

  // Update feature status
  updateFeatureStatus: async (moduleId: string, featureId: string, status: string) => {
    const response = await api.put(`/odos/modules/${moduleId}/features/${featureId}`, { status });
    return response.data;
  },

  // Update feature by index
  updateFeatureByIndex: async (moduleId: string, featureIndex: number, featureData: any) => {
    const response = await api.put(`/odos/modules/${moduleId}/features/index/${featureIndex}`, featureData);
    return response.data;
  },

  // Add issue
  addIssue: async (moduleId: string, issue: Partial<ODOSIssue>) => {
    const response = await api.post(`/odos/modules/${moduleId}/issues`, issue);
    return response.data;
  },

  // Resolve issue
  resolveIssue: async (moduleId: string, issueId: string) => {
    const response = await api.put(`/odos/modules/${moduleId}/issues/${issueId}/resolve`);
    return response.data;
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    const response = await api.get('/odos/modules/stats/dashboard');
    return response.data;
  },

  // Get users for team assignment
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  }
};

// Activity API Functions
export const odosActivityService = {
  // Get all activities
  getActivities: async (params?: { module?: string; status?: string; priority?: string; assignedTo?: string; type?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.module) queryParams.append('module', params.module);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.assignedTo) queryParams.append('assignedTo', params.assignedTo);
    if (params?.type) queryParams.append('type', params.type);
    
    const response = await api.get(`/odos/activities?${queryParams.toString()}`);
    return response.data;
  },

  // Get single activity
  getActivity: async (id: string) => {
    const response = await api.get(`/odos/activities/${id}`);
    return response.data;
  },

  // Create activity
  createActivity: async (activityData: Partial<ODOSActivity>) => {
    const response = await api.post('/odos/activities', activityData);
    return response.data;
  },

  // Update activity
  updateActivity: async (id: string, activityData: Partial<ODOSActivity>) => {
    const response = await api.put(`/odos/activities/${id}`, activityData);
    return response.data;
  },

  // Delete activity
  deleteActivity: async (id: string) => {
    const response = await api.delete(`/odos/activities/${id}`);
    return response.data;
  },

  // Get activities by module
  getActivitiesByModule: async (moduleId: string) => {
    const response = await api.get(`/odos/activities/module/${moduleId}`);
    return response.data;
  },

  // Get user's activities
  getUserActivities: async () => {
    const response = await api.get('/odos/activities/user/my-activities');
    return response.data;
  },

  // Get overdue activities
  getOverdueActivities: async () => {
    const response = await api.get('/odos/activities/overdue');
    return response.data;
  },

  // Add comment
  addComment: async (activityId: string, text: string) => {
    const response = await api.post(`/odos/activities/${activityId}/comments`, { text });
    return response.data;
  },

  // Add blocker
  addBlocker: async (activityId: string, description: string) => {
    const response = await api.post(`/odos/activities/${activityId}/blockers`, { description });
    return response.data;
  },

  // Resolve blocker
  resolveBlocker: async (activityId: string, blockerId: string, resolution: string) => {
    const response = await api.put(`/odos/activities/${activityId}/blockers/${blockerId}`, { resolution });
    return response.data;
  },

  // Complete acceptance criteria
  completeAcceptanceCriteria: async (activityId: string, criteriaId: string) => {
    const response = await api.put(`/odos/activities/${activityId}/acceptance/${criteriaId}`);
    return response.data;
  },

  // Get activity stats
  getActivityStats: async () => {
    const response = await api.get('/odos/activities/stats/summary');
    return response.data;
  }
};

export default {
  modules: odosModuleService,
  activities: odosActivityService
};
